"use client";

import { c as _c } from "react/compiler-runtime";
import type { PrincipalRoleResponseDto } from "@dc/gateway-jsonapi";
import { useMemo } from "react";
import { createAbilityFromRules, subject as createSubject } from "@/lib/ability/createAbility";
import type { AbilityRule, AppAbility, SubjectWithScope } from "@/lib/ability/types";
import { useMe } from "./useMe";
export interface SubjectWithScopeId {
  scopeType: string;
  scopeId?: string;
  principalId?: string;
}
export interface UseAbilityReturn {
  ability: AppAbility | null;
  error: Error | null;
  isLoading: boolean;
  can: (action: string, subject: SubjectWithScopeId, field?: string) => boolean;
  cannot: (action: string, subject: SubjectWithScopeId, field?: string) => boolean;
  roles: PrincipalRoleResponseDto[] | null;
  /** Scope ids at which the user holds an admin-tier role assigned directly (for admin-grant gating). */
  adminScopeIds?: Set<string>;
}
export function useAbility() {
  const $ = _c(18);
  const {
    me: userProfile,
    error,
    isLoading
  } = useMe();
  let t0;
  if ($[0] !== userProfile?.included) {
    t0 = userProfile?.included?.filter(_temp).map(_temp2);
    $[0] = userProfile?.included;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const roles = t0 as PrincipalRoleResponseDto[] | null;
  let scopeIds;
  if ($[2] !== userProfile?.included) {
    const included = userProfile?.included ?? [];
    const adminRoleIds = new Set();
    for (const item_1 of included) {
      if (item_1.type !== "roles") {
        continue;
      }
      const attrs = (item_1.attributes ?? {}) as {
        name?: string;
        type?: string;
      };
      if (attrs.type === "admin" || attrs.name?.endsWith("Administrator")) {
        adminRoleIds.add(item_1.id);
      }
    }
    scopeIds = new Set();
    for (const item_2 of included) {
      if (item_2.type !== "principalRoles") {
        continue;
      }
      const attrs_0 = (item_2.attributes ?? {}) as {
        roleId?: string;
        scopeId?: string;
        deletedAt?: string | null;
      };
      if (!attrs_0.deletedAt && attrs_0.scopeId && attrs_0.roleId && adminRoleIds.has(attrs_0.roleId)) {
        scopeIds.add(attrs_0.scopeId);
      }
    }
    $[2] = userProfile?.included;
    $[3] = scopeIds;
  } else {
    scopeIds = $[3];
  }
  const adminScopeIds = scopeIds;
  let t1;
  bb0: {
    const rules = userProfile?.meta?.abilities;
    if (!rules) {
      t1 = null;
      break bb0;
    }
    const t2 = rules as AbilityRule[];
    let t3;
    if ($[4] !== t2) {
      t3 = createAbilityFromRules(t2);
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t1 = t3;
  }
  const ability = t1;
  let t2;
  if ($[6] !== ability) {
    t2 = (action, subjectParam, field) => {
      if (!ability) {
        return false;
      }
      const caslSubject = subjectParam.principalId ? createSubject(subjectParam.scopeType, {
        principalId: subjectParam.principalId
      }) as SubjectWithScope : subjectParam.scopeId ? createSubject(subjectParam.scopeType, {
        scopeId: subjectParam.scopeId
      }) as SubjectWithScope : subjectParam.scopeType;
      return ability.can(action, caslSubject, field);
    };
    $[6] = ability;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const can = t2;
  let t3;
  if ($[8] !== ability) {
    t3 = (action_0, subjectParam_0, field_0) => {
      if (!ability) {
        return true;
      }
      const caslSubject_0 = subjectParam_0.principalId ? createSubject(subjectParam_0.scopeType, {
        principalId: subjectParam_0.principalId
      }) as SubjectWithScope : subjectParam_0.scopeId ? createSubject(subjectParam_0.scopeType, {
        scopeId: subjectParam_0.scopeId
      }) as SubjectWithScope : subjectParam_0.scopeType;
      return ability.cannot(action_0, caslSubject_0, field_0);
    };
    $[8] = ability;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const cannot = t3;
  const t4 = error as Error | null;
  let t5;
  if ($[10] !== ability || $[11] !== adminScopeIds || $[12] !== can || $[13] !== cannot || $[14] !== isLoading || $[15] !== roles || $[16] !== t4) {
    t5 = {
      ability,
      error: t4,
      isLoading,
      can,
      cannot,
      roles,
      adminScopeIds
    };
    $[10] = ability;
    $[11] = adminScopeIds;
    $[12] = can;
    $[13] = cannot;
    $[14] = isLoading;
    $[15] = roles;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function _temp2(item_0) {
  return item_0.attributes as PrincipalRoleResponseDto;
}
function _temp(item) {
  return item.type === "principalRoles";
}